import React, {
  ReactElement,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import MuxPlayer from '@mux/mux-player-react'
import { TranscriptEditor } from 'src/v3/components/transcript-editor/transcript-editor'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import { selectUserID } from 'src/v3/settings/settings.slice'
import {
  fetchCourseV2Videos,
  selectCourseV2VideosByCourseID,
  selectCourseV2VideosStatusByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'

interface PipelineTranscriptEditorModalProps {
  open: boolean
  onClose: () => void
  videoId: string
  courseId: string
}

let pipelineEndpoints = (videoId: string) => ({
  transcript: `/api/pipeline/planning/courses/videos/${videoId}/transcript`,
  transcriptCorrected: `/api/pipeline/planning/courses/videos/${videoId}/transcript/corrected`,
  transcriptPut: `/api/pipeline/planning/courses/videos/${videoId}/transcript/put`,
  vocabulary: `/api/pipeline/planning/video/vocabulary`,
})

export function PipelineTranscriptEditorModal({
  open,
  onClose,
  videoId,
  courseId,
}: PipelineTranscriptEditorModalProps): ReactElement {
  let dispatch = useAppDispatch()
  let userID = useAppSelector(selectUserID)
  let videosStatus = useAppSelector(
    selectCourseV2VideosStatusByCourseID(courseId),
  )
  let videos = useAppSelector(selectCourseV2VideosByCourseID(courseId))
  let video = videos.find((v) => v.id === videoId)

  let [currentTime, setCurrentTime] = useState(0)
  let playerRef = useRef(null)
  let [isDirty, setIsDirty] = useState(false)
  let [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  useEffect(() => {
    if (!videosStatus || videosStatus === 'uninitialized') {
      dispatch(fetchCourseV2Videos(courseId))
    }
  }, [videosStatus, courseId, dispatch])

  let handleSeek = useCallback(
    (time: number) => {
      if (playerRef.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line fp/no-mutation
        playerRef.current.currentTime = time
      }
    },
    [playerRef],
  )

  let handleTimeUpdate = (event: any) => {
    let player = event.target
    setCurrentTime(player.currentTime)
  }

  let requestClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      onClose()
    }
  }

  let handleConfirmDiscard = () => {
    setShowDiscardConfirm(false)
    setIsDirty(false)
    onClose()
  }

  if (!video) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              height: '80vh',
              maxWidth: 'none',
              maxHeight: 'none',
            },
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading video...</Typography>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">Edit Transcript: {video.name}</Typography>
        <IconButton
          onClick={requestClose}
          size="small"
          aria-label="Close transcript editor"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Paper elevation={3} sx={{ mb: 3, flexShrink: 0 }}>
          <Box sx={{ height: '300px', maxHeight: '300px' }}>
            <MuxPlayer
              ref={playerRef}
              title={video.name}
              src={`${video.fileName.replace('mp4', 'm3u8')}`}
              accentColor="#f9ea4f"
              primaryColor="#6694E9"
              backwardSeekOffset={10}
              forwardSeekOffset={10}
              metadata={{
                video_id: video.id,
                video_title: video.name,
                viewer_user_id: userID,
              }}
              onTimeUpdate={handleTimeUpdate}
              streamType="on-demand"
              style={{ height: '100%', width: '100%', maxHeight: '300px' }}
            />
          </Box>
        </Paper>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TranscriptEditor
            currentTime={currentTime}
            onSeek={handleSeek}
            videoId={videoId}
            endpoints={pipelineEndpoints(videoId)}
            onDirtyChange={setIsDirty}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={requestClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      <Dialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard Unsaved Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved transcript edits. Closing now will discard them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardConfirm(false)}>
            Keep Editing
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDiscard}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default PipelineTranscriptEditorModal
